import { useEffect, useState } from "react";
import { loadPasswordEstimator, meterReading, type PasswordVerdict } from "./passwordStrength";

/**
 * The meter under a new account password (Sign up, Reset password).
 *
 * Shown from the first character typed. One bar that fills as the password
 * gets harder to guess, a label, and one line of advice while the password
 * would be refused. `rulesMet` is whether the checklist above is complete:
 * until it is, the reading stops at "Fair" (meterReading, passwordStrength.ts),
 * so the meter never calls a password strong that the page will refuse.
 *
 * Nothing until something is typed. The estimator loads when this first mounts
 * (passwordStrength.ts); until it has, and if it never does, the meter shows
 * nothing and the page falls back to the server's rules.
 */
export default function PasswordStrengthMeter(props: {
  password: string;
  userInputs: string[];
  rulesMet: boolean;
  onVerdict?: (verdict: PasswordVerdict | null) => void;
}) {
  const [estimate, setEstimate] = useState<((p: string, u?: string[]) => PasswordVerdict) | null>(null);

  useEffect(() => {
    let live = true;
    loadPasswordEstimator()
      .then((fn) => live && setEstimate(() => fn))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const verdict = estimate && props.password ? estimate(props.password, props.userInputs) : null;
  const inputsKey = props.userInputs.join("\n");

  useEffect(() => {
    props.onVerdict?.(verdict);
    // The verdict is derived from these; the callback identity is not a reason to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate, props.password, inputsKey]);

  if (!verdict) return null;

  const reading = meterReading(verdict, props.rulesMet);
  const colour = reading.score >= 3 ? "bg-success" : reading.score >= 2 ? "bg-warning" : "bg-danger";

  return (
    <div className="mt-2" aria-live="polite">
      <div className="h-1.5 overflow-hidden rounded-full bg-line" aria-hidden="true">
        <div className={`h-full rounded-full transition-[width] duration-150 ${colour}`} style={{ width: `${reading.fill}%` }} />
      </div>
      <div className="mt-1 text-xs text-fg-soft">
        Strength: <span className={reading.score >= 3 ? "text-success" : "text-fg-body"}>{reading.label}</span>
      </div>
      {reading.advice && <div className="mt-0.5 text-xs text-fg-hint">{reading.advice}</div>}
    </div>
  );
}
